import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  formatDateStamp,
  type AdminSettings,
  type Corner,
  type LightingMode,
  type LightingSettings,
  type LogoSettings,
} from '../shared/admin.ts';
import {
  MAX_COUNTDOWN_COLORS,
  countdownColorFor,
  scaleColor,
  type LedSettings,
  type WipeDirection,
} from '../shared/leds.ts';
import {
  adminApi,
  type AdminState,
  type BoothPresence,
  type ThemeChoice,
} from './api.ts';
import { desktop, isDesktop } from './desktop.ts';
import { DevSection } from './DevSection.tsx';
import { PhotosSection } from './PhotosSection.tsx';
import './admin.css';

const CORNERS: { value: Corner; label: string }[] = [
  { value: 'bottom-left', label: 'Bas gauche' },
  { value: 'bottom-center', label: 'Bas centre' },
  { value: 'bottom-right', label: 'Bas droite' },
  { value: 'top-left', label: 'Haut gauche' },
  { value: 'top-right', label: 'Haut droite' },
];

type Status = { kind: 'idle' | 'busy' | 'ok' | 'error'; message?: string };


export default function AdminApp() {
  const [ready, setReady] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [version, setVersion] = useState('');
  const [user, setUser] = useState<{ name: string; email: string } | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [state, setState] = useState<AdminState | null>(null);
  const [themes, setThemes] = useState<ThemeChoice[]>([]);
  const [previewTheme, setPreviewTheme] = useState('');
  const [booth, setBooth] = useState<BoothPresence | null>(null);
  /** Firestore injoignable depuis l'admin : ce n'est pas la borne qui manque. */
  const [boothError, setBoothError] = useState('');

  const loadBooth = useCallback(async () => {
    try {
      setBooth(await adminApi.booth());
      setBoothError('');
    } catch (error) {
      setBooth(null);
      setBoothError((error as Error).message);
    }
  }, []);
  const [quitPrompt, setQuitPrompt] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [tab, setTab] = useState<'client' | 'photos' | 'dev'>(() =>
    window.location.hash === '#dev' ? 'dev' : window.location.hash === '#photos' ? 'photos' : 'client',
  );


  const refresh = useCallback(async () => {
    setState(await adminApi.load());
  }, []);

  useEffect(() => {
    const boot = async () => {
      try {
        const session = await adminApi.session();
        setConfigured(session.configured);
        setAuthenticated(session.authenticated);
        setVersion(session.version ?? '');
        setUser(session.user ?? null);
        if (session.authenticated) {
          await refresh();
          const list = await adminApi.themes().catch(() => []);
          setThemes(list);
          setPreviewTheme((current) => current || list[0]?.id || '');
          await loadBooth();
        }
      } catch (error) {
        setStatus({ kind: 'error', message: (error as Error).message });
      } finally {
        setReady(true);
      }
    };
    void boot();
  }, [refresh, loadBooth]);

  /**
   * La borne publie son état toutes les minutes. On le relit au même rythme,
   * pour la voir décrocher sans recharger la page.
   */
  useEffect(() => {
    if (!authenticated) return;
    const timer = setInterval(() => {
      void loadBooth();
    }, 30_000);
    return () => clearInterval(timer);
  }, [authenticated, loadBooth]);

  /**
   * Enveloppe commune : état occupé, message d'erreur, rechargement. Une action
   * qui renvoie une chaîne l'utilise comme message de succès, ce qui permet de
   * remonter un détail (résultat de synchro) plutôt qu'un « fait » générique.
   */
  const run = useCallback(
    async (label: string, action: () => Promise<unknown>, reload = true) => {
      setStatus({ kind: 'busy', message: label });
      try {
        const outcome = await action();
        if (reload) await refresh();
        setStatus({
          kind: 'ok',
          message: typeof outcome === 'string' ? outcome : `${label} — fait`,
        });
      } catch (error) {
        setStatus({ kind: 'error', message: (error as Error).message });
      }
    },
    [refresh],
  );

  /**
   * Les curseurs émettent un événement par pixel de déplacement. Sans
   * temporisation, chaque mouvement déclencherait un enregistrement et un rendu
   * d'aperçu côté serveur. On accumule les modifications et on n'écrit qu'une
   * fois le réglage stabilisé.
   */
  const pending = useRef<Record<string, unknown>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const patch = useCallback(
    (partial: Record<string, unknown>) => {
      for (const [key, value] of Object.entries(partial)) {
        const previous = pending.current[key];
        pending.current[key] =
          Array.isArray(value) || typeof value !== 'object' || value === null
            ? value
            : { ...(previous as object | undefined), ...value };
      }

      // Retour visuel immédiat : l'état local suit le curseur sans attendre
      // l'aller-retour serveur.
      //
      // La fusion se fait champ par champ contre l'état courant. Un simple
      // étalement écrasait l'objet entier : cocher « écran de veille »
      // n'envoyait que `{ enabled }`, et `videos` disparaissait de l'état local
      // jusqu'au retour du serveur — le temps de faire planter tout ce qui le
      // lisait.
      setState((current) => {
        if (!current) return current;

        const settings = { ...current.settings } as Record<string, unknown>;
        for (const [key, value] of Object.entries(pending.current)) {
          const previous = settings[key];
          settings[key] =
            Array.isArray(value) || typeof value !== 'object' || value === null
              ? value
              : { ...(previous as object | undefined), ...value };
        }

        return { ...current, settings: settings as unknown as AdminSettings };
      });

      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const payload = pending.current;
        pending.current = {};
        void run('Enregistrement', () => adminApi.save(payload));
      }, 350);
    },
    [run],
  );

  if (!ready) return <div className="admin admin--center">Chargement…</div>;

  if (!configured) {
    return (
      <div className="admin admin--center">
        <div className="admin__panel">
          <h1>Admin désactivée</h1>
          <p>
            Définissez <code>OIDC_CLIENT_SECRET</code> et <code>AUTH_URL</code> sur le service,
            et faites enregistrer l’URI de redirection <code>AUTH_URL/callback</code>.
          </p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <LoginView status={status} />
    );
  }

  if (!state) return <div className="admin admin--center">Chargement des réglages…</div>;

  const { settings, files } = state;

  return (
    <div className="admin">
      <header className="admin__bar">
        <div className="admin__bar-left">
          <h1>
            Photobooth — administration
            {tab === 'dev' && version && <span className="admin__version">v{version}</span>}
          </h1>
          {(
          <nav className="admin__tabs">
            <button
              type="button"
              className={tab === 'client' ? 'admin__tab is-active' : 'admin__tab'}
              onClick={() => setTab('client')}
            >
              Paramètres
            </button>
            <button
              type="button"
              className={tab === 'photos' ? 'admin__tab is-active' : 'admin__tab'}
              onClick={() => setTab('photos')}
            >
              Photos
            </button>
            <button
              type="button"
              className={tab === 'dev' ? 'admin__tab is-active' : 'admin__tab'}
              onClick={() => setTab('dev')}
            >
              Paramètres avancés
            </button>
          </nav>
        )}
        </div>
        <div className="admin__bar-actions">
          <StatusPill status={status} />
          {/* Seulement dans la fenêtre ouverte par la borne : depuis un poste
              quelconque, il n'y a pas de borne où revenir. */}
          {desktop()?.closeAdmin && (
            <button
              type="button"
              className="btn btn--ghost"
              title="Ctrl+F9"
              onClick={() => void desktop()?.closeAdmin?.()}
            >
              ← Retour à la borne
            </button>
          )}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() =>
              run('Synchronisation des thèmes', async () => {
                const { report } = await adminApi.refreshTheme();
                if (report.offline) return `Hors ligne — copie locale conservée`;
                if (!report.discovered) return 'Aucune source configurée';
                const changed = report.themes.filter((t) => t.downloaded || t.removed);
                return (
                  `${report.discovered} thème(s) · ` +
                  (changed.length
                    ? `${changed.length} mis à jour : ${changed.map((t) => t.themeId).join(', ')}`
                    : 'tous à jour') +
                  (report.pruned?.length ? ` · ${report.pruned.length} retiré(s)` : '')
                );
              }, false)
            }
          >
            Synchroniser les thèmes
          </button>
          {/* Juste avant la déconnexion : on voit sous quel compte on agit à
              l'endroit même où l'on cesse de l'être. Le nom peut manquer dans
              l'EAM — l'e-mail identifie alors tout aussi bien. */}
          {user && (
            <span className="admin__user" title={user.email}>
              {user.name || user.email}
            </span>
          )}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={async () => {
              await adminApi.logout();
              setAuthenticated(false);
              setUser(null);
              setState(null);
            }}
          >
            Déconnexion
          </button>
          {/* Seulement dans l'application de bureau : depuis un navigateur, il
              n'y a rien à arrêter. C'est la sortie prévue en fin d'événement,
              quand la borne tourne en plein écran sans barre de fenêtre. */}
          {isDesktop() && (
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => setQuitPrompt(true)}
            >
              Éteindre la borne
            </button>
          )}
        </div>
      </header>

      <main className={`admin__grid admin__grid--${tab}`} key={tab}>
        {tab === 'photos' ? (
          <PhotosSection />
        ) : tab === 'dev' ? (
          <DevSection
            onExit={() => {
              setTab('client');
              if (window.location.hash) window.location.hash = '';
            }}
          />
        ) : (
          <>
            <Summary
              settings={settings}
              booth={booth}
              boothError={boothError}
              themes={themes}
              themeId={previewTheme}
            />

            <header className="admin__group">
              <h2>La photo</h2>
              <p>Ce que porte chaque photo : aperçu, fond du détourage, logos et date.</p>
            </header>

            <PreviewSection
              themes={themes}
              themeId={previewTheme}
              version={settings.updatedAt ?? 'init'}
              onThemeChange={setPreviewTheme}
            />

            <BackgroundSection
              settings={settings}
              available={files.backgrounds}
              onChange={(cutoutBackground) => patch({ cutoutBackground })}
              onUpload={(file, onProgress) =>
                run('Envoi du fond', async () => {
                  const { file: name } = await adminApi.uploadBackground(file, onProgress);
                  // Un fond que l'on vient d'envoyer, c'est un fond que l'on veut
                  // voir sur les photos : il est choisi et activé d'emblée.
                  await adminApi.save({ cutoutBackground: { enabled: true, file: name } });
                })
              }
              onDelete={(name) =>
                run('Suppression du fond', async () => {
                  await adminApi.deleteBackground(name);
                  if (settings.cutoutBackground.file === name) {
                    await adminApi.save({ cutoutBackground: { enabled: false, file: '' } });
                  }
                })
              }
            />

            <LogosSection
              settings={settings}
              available={files.logos}
              themeId={previewTheme}
              onThemeLogoChange={(themeLogo) => patch({ themeLogo })}
              onChange={(logos) => patch({ logos })}
              onUpload={(file, onProgress) =>
                run('Ajout du logo', () => adminApi.uploadLogo(file, onProgress))
              }
              onDelete={(name) =>
                run('Suppression du logo', async () => {
                  await adminApi.deleteLogo(name);
                  await adminApi.save({ logos: settings.logos.filter((l) => l.file !== name) });
                })
              }
            />

            <DateSection settings={settings} onChange={(dateStamp) => patch({ dateStamp })} />

            <header className="admin__group">
              <h2>La prise de vue</h2>
              <p>La lumière pendant le décompte et au moment de la photo.</p>
            </header>

            <LightingSection settings={settings} onChange={(lighting) => patch({ lighting })} />

            <LedSection
              settings={settings}
              booth={booth}
              onChange={(leds) => patch({ leds })}
              onTest={() =>
                run(
                  'Test du ruban',
                  async () => {
                    const { port } = await adminApi.testLeds(settings.leds);
                    return `Décompte et flash joués sur la borne (${port})`;
                  },
                  false,
                )
              }
            />

            <header className="admin__group">
              <h2>La borne au repos</h2>
              <p>Ce qui passe à l’écran entre deux groupes.</p>
            </header>

            <ScreensaverSection
              settings={settings}
              available={files.videos}
              onChange={(screensaver) => patch({ screensaver })}
              onUpload={(file, onProgress) =>
                run('Envoi de la vidéo', () => adminApi.uploadVideo(file, onProgress))
              }
              onDelete={(name) =>
                run('Suppression de la vidéo', async () => {
                  await adminApi.deleteVideo(name);
                  await adminApi.save({
                    screensaver: {
                      videos: settings.screensaver.videos.filter((v) => v !== name),
                    },
                  });
                })
              }
            />
          </>
        )}
      </main>

      {quitPrompt && (
        <div className="admin__modal" role="dialog" aria-modal>
          <div className="admin__panel">
            <h1>Éteindre la borne ?</h1>
            <p>
              L’application se fermera entièrement, écran compris. Les photos et les réglages
              sont conservés — il suffit de relancer <code>Photobooth.exe</code>.
            </p>
            <div className="card__actions card__actions--end">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setQuitPrompt(false)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => void desktop()?.quit()}
              >
                Éteindre
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/** « il y a 3 min », pour le dernier signe de vie de la borne. */
function ago(iso: string): string {
  const seconds = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (seconds < 90) return 'à l’instant';
  if (seconds < 3600) return `il y a ${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return `il y a ${Math.round(seconds / 3600)} h`;
  return new Date(iso).toLocaleString('fr-FR');
}

/**
 * Bandeau d'état, en tête de page.
 *
 * Ce que l'on vient vérifier entre deux visiteurs — la borne répond-elle, quel
 * thème tourne, la veille est-elle armée — tient ici, sans avoir à parcourir les
 * cartes. Le reste de la page sert à régler, celui-ci à constater.
 */
function Summary({
  settings,
  booth,
  boothError,
  themes,
  themeId,
}: {
  settings: AdminSettings;
  booth: BoothPresence | null;
  boothError: string;
  themes: ThemeChoice[];
  themeId: string;
}) {
  const saver = settings.screensaver;
  const videos = saver?.videos ?? [];
  const logos = settings.logos ?? [];
  const background = settings.cutoutBackground;
  const theme = themes.find((t) => t.id === themeId);

  const paper = booth?.printer?.source === 'printer' ? booth.printer : null;
  const leds = settings.leds;
  const logoCount = logos.length + (settings.themeLogo.enabled ? 1 : 0);

  return (
    <section className="summary">
      {/* L'admin ne parle à la borne qu'à travers Firestore : tout ce qu'elle
          règle ici n'arrive que si la borne est en ligne. */}
      <Stat
        label="Borne"
        level={boothError ? 'error' : booth?.online ? 'ok' : 'error'}
        value={boothError ? 'Firestore injoignable' : booth?.online ? 'En ligne' : 'Hors ligne'}
        note={
          boothError
            ? boothError
            : booth?.lastSeen
              ? `vue ${ago(booth.lastSeen)}${booth.media?.error ? ' · médias en échec' : ''}`
              : 'jamais connectée'
        }
      />

      {/* Seule une lecture de l'imprimante compte : jamais le décompte d'une
          borne, qu'un ancien document peut encore porter. */}
      <Stat
        label="Papier"
        level={!paper ? 'warn' : paper.remaining <= 0 ? 'error' : paper.low ? 'warn' : 'ok'}
        value={paper ? `${paper.remaining} tirages` : '—'}
        note={paper ? `restants, lus sur l’imprimante · ${ago(paper.checkedAt)}` : 'aucune lecture de l’imprimante reçue'}
        title={paper?.detail}
      />

      <Stat
        label="Ruban LED"
        level={!leds.enabled ? 'idle' : !booth?.leds ? 'warn' : booth.leds.connected ? 'ok' : 'error'}
        value={!leds.enabled ? 'Désactivé' : !booth?.leds ? 'Inconnu' : booth.leds.connected ? 'Connecté' : 'Absent'}
        note={
          booth?.leds?.connected
            ? leds.schedule.enabled
              ? `allumé de ${leds.schedule.on} à ${leds.schedule.off}`
              : 'allumé en permanence'
            : booth?.leds?.detail || 'état pas encore publié par la borne'
        }
        title={booth?.leds?.detail}
      />

      <Stat
        label="Thème"
        level={theme ? 'ok' : 'warn'}
        value={theme?.displayName ?? '—'}
        note={themes.length > 1 ? `${themes.length} thèmes sur la borne` : themes.length ? 'seul thème installé' : 'aucun thème publié'}
      />

      <Stat
        label="Fond du détourage"
        level={background.enabled && background.file ? 'ok' : 'idle'}
        value={background.enabled && background.file ? 'Activé' : 'Transparent'}
        note={background.enabled && background.file ? background.file : 'PNG sans fond'}
        title={background.file}
      />

      <Stat
        label="Date et logos"
        level={settings.dateStamp.enabled || logoCount ? 'ok' : 'idle'}
        value={settings.dateStamp.enabled ? 'Date activée' : 'Sans date'}
        note={logoCount ? `${logoCount} logo(s) posé(s)` : 'aucun logo posé'}
      />

      <Stat
        label="Écran de veille"
        level={!saver?.enabled ? 'idle' : videos.length ? 'ok' : 'warn'}
        value={saver?.enabled ? `${saver.idleSeconds} s` : 'Désactivé'}
        note={
          saver?.enabled && !videos.length
            ? 'aucune vidéo — la veille ne se déclenchera pas'
            : `${videos.length} vidéo(s) en lecture`
        }
      />
    </section>
  );
}

/**
 * Indicateur du bandeau. Vert : en ordre ; orange : à vérifier ; rouge : à
 * régler avant l'ouverture des portes ; neutre : fonction désactivée.
 */
function Stat({
  label,
  level,
  value,
  note,
  title,
}: {
  label: string;
  level: 'ok' | 'warn' | 'error' | 'idle';
  value: string;
  note: string;
  title?: string;
}) {
  return (
    <div className={`stat stat--${level}`}>
      <span className="stat__label">
        <span className="stat__dot" aria-hidden />
        {label}
      </span>
      <strong className="stat__value stat__value--text">{value}</strong>
      <span className="stat__note" title={title ?? note}>
        {note}
      </span>
    </div>
  );
}

function StatusPill({ status }: { status: Status }) {
  if (status.kind === 'idle') return null;
  return <span className={`pill pill--${status.kind}`}>{status.message}</span>;
}

/** Nom du produit, tel qu'il doit s'écrire — capitales comprises. */
const PRODUCT = 'THALES PHOTOBOOTH ADMIN';
const MIP_LOGO = 'https://eam.wearemip.com/mip-logo.svg';

/** Interlettrage du nom : valeur de départ, et plancher du resserrage. */
const TRACK_FROM = 0.06;
const TRACK_TO = 0;

/** Corps du nom : valeur nominale, et plancher au-delà duquel il ferait légende. */
const BRAND_SIZE = 24;
const BRAND_SIZE_MIN = 14;

/**
 * Connexion. L'écran ne parle ni à Google ni à un formulaire : un seul bouton
 * envoie vers l'EAM, qui est le fournisseur d'identité. La composition est celle
 * de la colonne gauche de l'EAM, posée seule et centrée.
 */
function LoginView({ status }: { status: Status }) {
  const failed = new URLSearchParams(window.location.search).get('auth_error');
  const message = failed === 'forbidden'
    ? 'This account is not allowed on this booth.'
    : failed
      ? 'MIP sign-in failed. Please try again.'
      : status.kind === 'error'
        ? status.message
        : '';

  /**
   * Le logo conditionne la mesure du nom : il prend la place qui lui reste.
   * Tant qu'il n'est pas chargé, le caler reviendrait à le mesurer contre une
   * largeur nulle.
   */
  const [logo, setLogo] = useState<'pending' | 'ready' | 'failed'>('pending');
  const nameRef = useRef<HTMLSpanElement>(null);

  /**
   * Le nom tient sur une ligne, c'est la contrainte dure : on resserre d'abord
   * l'interlettrage, levier prévu pour cela.
   *
   * Il ne suffit pas ici. « THALES PHOTOBOOTH ADMIN » demande 355 px sans
   * interlettrage là où la colonne en laisse 268 : la composition vient d'une
   * page dont le mot de marque fait trois lettres, pas vingt-trois. Plutôt que
   * de tronquer le nom ou de le renvoyer à la ligne, on réduit le corps juste
   * assez — et jamais au point d'en faire une légende du logo.
   */
  useLayoutEffect(() => {
    const el = nameRef.current;
    const row = el?.parentElement;
    if (!el || !row) return;

    const fit = () => {
      el.style.fontSize = `${BRAND_SIZE}px`;
      for (let step = 0; step <= 12; step += 1) {
        const track = Math.max(TRACK_TO, TRACK_FROM - step * 0.005);
        el.style.letterSpacing = `${track}em`;
        if (el.scrollWidth <= el.clientWidth) return;
        if (track === TRACK_TO) break;
      }
      if (!el.scrollWidth) return;
      const scale = el.clientWidth / el.scrollWidth;
      el.style.fontSize = `${Math.max(BRAND_SIZE_MIN, Math.floor(BRAND_SIZE * scale))}px`;
    };

    fit();
    // `fonts.ready` résout trop tôt — avant qu'Archivo ne soit réellement
    // chargée — et la mesure se ferait alors sur les métriques de la police de
    // secours, plus étroites. On demande la fonte précise et on recale dessus.
    void document.fonts
      ?.load(`600 ${BRAND_SIZE}px Archivo`)
      .then(fit)
      .catch(() => {});

    const observer = new ResizeObserver(fit);
    observer.observe(row);
    return () => observer.disconnect();
  }, [logo]);

  return (
    <div className="signin">
      {/* Hors composition : n'existe que dans la fenêtre ouverte par la borne,
          où il n'y a pas de barre de titre pour revenir en arrière. */}
      <button
        type="button"
        className="admin__close"
        aria-label="Close"
        title="Close"
        onClick={() => {
          const close = desktop()?.closeAdmin;
          if (close) void close();
          else window.close();
        }}
      >
        ×
      </button>

      <div className="signin__col">
        {/* Le logo dit qui édite, le nom dit quel outil. Si le fichier ne charge
            pas, le nom suffit — et le filet n'a plus rien à séparer. */}
        <div className="signin__brand">
          {logo !== 'failed' && (
            <>
              <img
                className="signin__brand-logo"
                src={MIP_LOGO}
                alt="MIP"
                onLoad={() => setLogo('ready')}
                onError={() => setLogo('failed')}
              />
              <span className="signin__brand-rule" aria-hidden="true" />
            </>
          )}
          <span className="signin__brand-name" ref={nameRef}>
            {PRODUCT}
          </span>
        </div>

        <div className="signin__head">
          <h1 className="signin__title">Sign in to {PRODUCT}</h1>
          <p className="signin__lede">
            Set the logos, date, screensaver videos and themes shown on the booth.
          </p>
        </div>

        <a className="signin__button" href="/api/auth/login">
          <span className="signin__chip">
            <img src={MIP_LOGO} alt="" />
          </span>
          Sign in with MIP
        </a>

        <p className="signin__note">
          Sign-in is handled by MIP, restricted to @wearemip.com accounts.
        </p>

        {message && <p className="signin__error">{message}</p>}

        <p className="signin__foot">Trouble signing in? Contact your MIP contact.</p>

        {/* Sur la borne seulement : sur un réseau d'entreprise, la connexion
            passe par le proxy, qui se règle sur la borne — donc avant le login. */}
        {desktop()?.openNetworkSettings && (
          <button
            type="button"
            className="signin__network"
            onClick={() => void desktop()?.openNetworkSettings?.()}
          >
            Booth network settings (proxy) <kbd>Ctrl+F9</kbd>
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Aperçu rendu par le serveur, avec la fonction d'incrustation de production :
 * ce qui s'affiche ici est exactement ce qui sera posé sur les photos.
 */
function PreviewSection({
  themes,
  themeId,
  version,
  onThemeChange,
}: {
  themes: ThemeChoice[];
  themeId: string;
  version: string;
  onThemeChange: (id: string) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const src = adminApi.previewUrl(themeId, version);

  useEffect(() => {
    setLoading(true);
    setFailed(false);
  }, [src]);

  return (
    <section className="card card--wide">
      <header>
        <h2>Aperçu sur une photo</h2>
        {themes.length > 1 && (
          <label className="preview__picker">
            Thème
            <select value={themeId} onChange={(e) => onThemeChange(e.target.value)}>
              {themes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.displayName}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>

      <p className="card__hint">
        Image d’exemple du thème, avec vos réglages appliqués. Seuls les rendus décor sont
        signés ainsi — une photo détourée reste vierge.
      </p>

      <div className={`preview ${loading ? 'is-loading' : ''}`}>
        {failed ? (
          <p className="card__empty">Aperçu indisponible.</p>
        ) : (
          <img
            src={src}
            alt="Aperçu de l’incrustation"
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setFailed(true);
            }}
          />
        )}
      </div>
    </section>
  );
}

function DateSection({
  settings,
  onChange,
}: {
  settings: AdminSettings;
  onChange: (patch: Partial<AdminSettings['dateStamp']>) => void;
}) {
  const date = settings.dateStamp;
  const preview = formatDateStamp(date, new Date());

  return (
    <section className="card card--half">
      <header>
        <h2>Date sur la photo</h2>
        <Toggle checked={date.enabled} onChange={(enabled) => onChange({ enabled })} />
      </header>

      <p className="card__hint">
        Incrustée en bas des rendus décor. Les photos détourées restent vierges : la date sera posée
        au moment du compositing.
      </p>

      <div className="card__preview">{preview || '—'}</div>

      <div className="field-grid">
        <label>
          Contenu
          <select value={date.mode} onChange={(e) => onChange({ mode: e.target.value as 'auto' })}>
            <option value="auto">Date du jour</option>
            <option value="custom">Texte libre</option>
          </select>
        </label>

        {date.mode === 'custom' ? (
          <label className="field--wide">
            Texte
            <input
              value={date.customText}
              placeholder="Salon Eurosatory 2026"
              onChange={(e) => onChange({ customText: e.target.value })}
            />
          </label>
        ) : (
          <>
            <label className="field--check">
              <input
                type="checkbox"
                checked={date.includeWeekday}
                onChange={(e) => onChange({ includeWeekday: e.target.checked })}
              />
              Jour de la semaine
            </label>
            <label className="field--check">
              <input
                type="checkbox"
                checked={date.includeTime}
                onChange={(e) => onChange({ includeTime: e.target.checked })}
              />
              Heure
            </label>
          </>
        )}

        <label>
          Position
          <select
            value={date.position}
            onChange={(e) => onChange({ position: e.target.value as Corner })}
          >
            {CORNERS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          Taille — {(date.fontScale * 100).toFixed(1)} % de la hauteur
          <input
            type="range"
            min={0.015}
            max={0.07}
            step={0.002}
            value={date.fontScale}
            onChange={(e) => onChange({ fontScale: Number(e.target.value) })}
          />
        </label>

        <label>
          Couleur
          <input
            type="color"
            value={date.color}
            onChange={(e) => onChange({ color: e.target.value })}
          />
        </label>

        <label className="field--check">
          <input
            type="checkbox"
            checked={date.shadow}
            onChange={(e) => onChange({ shadow: e.target.checked })}
          />
          Bandeau sombre derrière le texte
        </label>

        <label className="field--check">
          <input
            type="checkbox"
            checked={date.uppercase}
            onChange={(e) => onChange({ uppercase: e.target.checked })}
          />
          Majuscules
        </label>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function LogosSection({
  settings,
  available,
  themeId,
  onThemeLogoChange,
  onChange,
  onUpload,
  onDelete,
}: {
  settings: AdminSettings;
  available: string[];
  themeId: string;
  onThemeLogoChange: (patch: Partial<AdminSettings['themeLogo']>) => void;
  onChange: (logos: LogoSettings[]) => void;
  onUpload: (file: File, onProgress: (ratio: number) => void) => Promise<unknown>;
  onDelete: (name: string) => void;
}) {
  const placed = settings.logos;
  const unplaced = available.filter((file) => !placed.some((logo) => logo.file === file));

  const update = (index: number, partial: Partial<LogoSettings>) =>
    onChange(placed.map((logo, i) => (i === index ? { ...logo, ...partial } : logo)));

  return (
    <section className="card card--half">
      <header>
        <h2>Logos sur la photo</h2>
        <UploadButton accept="image/*" label="Ajouter un logo" onFile={onUpload} />
      </header>

      <p className="card__hint">
        Le logo Thales vient du thème : il suffit de l’activer. Les logos ajoutés ici sont
        envoyés dans le stockage du projet, puis recopiés sur la borne. Tous sont incrustés dans
        les photos finies — rendus décor et détourages posés sur un fond.
      </p>

      {/* Vignettes plutôt que lignes : un logo se juge à l'œil — sa découpe, sa
          transparence, sa lisibilité sur fond clair — pas à son nom de fichier. */}
      <div className="tiles">
        <ThemeLogoTile
          settings={settings.themeLogo}
          themeId={themeId}
          onChange={onThemeLogoChange}
        />

        {placed.map((logo, index) => (
          <article className="tile" key={logo.file}>
            <div className="tile__media tile__media--logo">
              <img src={`/media/logos/${encodeURIComponent(logo.file)}`} alt="" />
              <span className="tile__badge">Sur la photo</span>
            </div>

            <strong className="tile__name" title={logo.file}>
              {logo.file}
            </strong>

            <div className="field-grid field-grid--tight">
              <label>
                Position
                <select
                  value={logo.position}
                  onChange={(e) => update(index, { position: e.target.value as Corner })}
                >
                  {CORNERS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Taille — {(logo.scale * 100).toFixed(0)} %
                <input
                  type="range"
                  min={0.03}
                  max={0.25}
                  step={0.005}
                  value={logo.scale}
                  onChange={(e) => update(index, { scale: Number(e.target.value) })}
                />
              </label>
              <label>
                Opacité — {(logo.opacity * 100).toFixed(0)} %
                <input
                  type="range"
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={logo.opacity}
                  onChange={(e) => update(index, { opacity: Number(e.target.value) })}
                />
              </label>
            </div>

            <div className="tile__actions">
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => onChange(placed.filter((_, i) => i !== index))}
              >
                Retirer de la photo
              </button>
              <button
                type="button"
                className="btn btn--danger btn--small"
                onClick={() => onDelete(logo.file)}
              >
                Supprimer
              </button>
            </div>
          </article>
        ))}

        {unplaced.map((file) => (
          <article className="tile tile--idle" key={file}>
            <div className="tile__media tile__media--logo">
              <img src={`/media/logos/${encodeURIComponent(file)}`} alt="" />
            </div>

            <strong className="tile__name" title={file}>
              {file}
            </strong>
            <span className="card__hint">Sur la borne, pas encore posé sur la photo.</span>

            <div className="tile__actions">
              <button
                type="button"
                className="btn btn--small"
                onClick={() =>
                  onChange([...placed, { file, position: 'bottom-right', scale: 0.09, opacity: 1 }])
                }
              >
                Poser sur la photo
              </button>
              <button
                type="button"
                className="btn btn--danger btn--small"
                onClick={() => onDelete(file)}
              >
                Supprimer
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/**
 * Logo de la marque, fourni par le thème : le logo Thales. Rien à envoyer, on
 * choisit seulement de le poser sur les photos, et comment.
 */
function ThemeLogoTile({
  settings,
  themeId,
  onChange,
}: {
  settings: AdminSettings['themeLogo'];
  themeId: string;
  onChange: (patch: Partial<AdminSettings['themeLogo']>) => void;
}) {
  const [missing, setMissing] = useState(false);

  return (
    <article className={settings.enabled ? 'tile' : 'tile tile--idle'}>
      <div className="tile__media tile__media--logo">
        {missing ? (
          <span className="card__hint">Aucun logo dans le thème</span>
        ) : (
          <img
            src={`/api/admin/theme-logo?themeId=${encodeURIComponent(themeId)}`}
            alt=""
            onError={() => setMissing(true)}
          />
        )}
        {settings.enabled && <span className="tile__badge">Sur la photo</span>}
      </div>

      <strong className="tile__name">Logo Thales — fourni par le thème</strong>

      <Toggle checked={settings.enabled} onChange={(enabled) => onChange({ enabled })} />

      {settings.enabled && (
        <div className="field-grid field-grid--tight">
          <label>
            Position
            <select
              value={settings.position}
              onChange={(e) => onChange({ position: e.target.value as Corner })}
            >
              {CORNERS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Taille — {(settings.scale * 100).toFixed(1)} %
            <input
              type="range"
              min={0.03}
              max={0.25}
              step={0.005}
              value={settings.scale}
              onChange={(e) => onChange({ scale: Number(e.target.value) })}
            />
          </label>
          <label>
            Opacité — {(settings.opacity * 100).toFixed(0)} %
            <input
              type="range"
              min={0.1}
              max={1}
              step={0.05}
              value={settings.opacity}
              onChange={(e) => onChange({ opacity: Number(e.target.value) })}
            />
          </label>
        </div>
      )}
    </article>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Fond posé derrière les photos détourées.
 *
 * Sans fond, un détourage sort en PNG transparent, pour un compositing
 * ultérieur. Avec un fond, la borne compose la photo finale elle-même et y pose
 * date et logos, comme sur un décor.
 */
function BackgroundSection({
  settings,
  available,
  onChange,
  onUpload,
  onDelete,
}: {
  settings: AdminSettings;
  available: string[];
  onChange: (patch: Partial<AdminSettings['cutoutBackground']>) => void;
  onUpload: (file: File, onProgress: (ratio: number) => void) => Promise<unknown>;
  onDelete: (name: string) => void;
}) {
  const background = settings.cutoutBackground;

  return (
    <section className="card card--side">
      <header>
        <h2>Fond du détourage</h2>
        <Toggle
          checked={background.enabled && Boolean(background.file)}
          onChange={(enabled) => onChange({ enabled, file: background.file || available[0] || '' })}
        />
      </header>

      <p className="card__hint">
        Photo posée derrière les personnes détourées. Elle couvre tout le cadre, centrée : les
        bords dépassant du 16/9 sont rognés, comme sur la vignette. Sans fond, la photo sort
        transparente.
      </p>

      <label>
        Harmonisation avec le fond — {Math.round(background.blend * 100)} %
        <input
          type="range"
          min={0}
          max={0.8}
          step={0.05}
          value={background.blend}
          onChange={(e) => onChange({ blend: Number(e.target.value) })}
        />
        <span className="card__hint">
          Rapproche les teintes et la lumière de la personne de celles du fond, avec un léger halo
          du décor sur les contours. 0 % = collage brut ; au-delà de 50 %, les visages prennent la
          couleur du décor.
        </span>
      </label>

      <div className="card__actions">
        <UploadButton accept="image/png,image/jpeg,image/webp" label="Importer une photo" onFile={onUpload} />
      </div>

      {!available.length && <p className="card__empty">Aucune photo de fond.</p>}

      <div className="tiles">
        {available.map((file) => {
          const current = background.enabled && background.file === file;
          return (
            <article className={current ? 'tile' : 'tile tile--idle'} key={file}>
              <div className="tile__media tile__media--cover">
                <img src={`/media/backgrounds/${encodeURIComponent(file)}`} alt="" />
                {current && <span className="tile__badge">En fond</span>}
              </div>

              <strong className="tile__name" title={file}>
                {file}
              </strong>

              <div className="tile__actions">
                {!current && (
                  <button
                    type="button"
                    className="btn btn--small"
                    onClick={() => onChange({ enabled: true, file })}
                  >
                    Utiliser comme fond
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn--danger btn--small"
                  onClick={() => onDelete(file)}
                >
                  Supprimer
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function ScreensaverSection({
  settings,
  available,
  onChange,
  onUpload,
  onDelete,
}: {
  settings: AdminSettings;
  available: string[];
  onChange: (patch: Partial<AdminSettings['screensaver']>) => void;
  onUpload: (file: File, onProgress: (ratio: number) => void) => Promise<unknown>;
  onDelete: (name: string) => void;
}) {
  const saver = settings.screensaver;
  const move = (index: number, delta: number) => {
    const next = [...saver.videos];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange({ videos: next });
  };

  return (
    <section className="card card--full">
      <header>
        <h2>Écran de veille</h2>
        <Toggle checked={saver.enabled} onChange={(enabled) => onChange({ enabled })} />
      </header>

      <p className="card__hint">
        Après {saver.idleSeconds} s sans interaction sur l’accueil, les vidéos passent en plein
        écran. Un contact ramène à l’accueil. Les vidéos sont recopiées sur la borne : la
        veille tourne même hors ligne.
      </p>

      <div className="field-grid">
        <label>
          Délai avant veille — {saver.idleSeconds} s
          <input
            type="range"
            min={15}
            max={300}
            step={5}
            value={saver.idleSeconds}
            onChange={(e) => onChange({ idleSeconds: Number(e.target.value) })}
          />
        </label>
        <label className="field--check">
          <input
            type="checkbox"
            checked={saver.muted}
            onChange={(e) => onChange({ muted: e.target.checked })}
          />
          Sans son
        </label>
      </div>

      <div className="card__actions">
        <UploadButton accept="video/*" label="Ajouter une vidéo" onFile={onUpload} />
      </div>

      {!available.length && <p className="card__empty">Aucune vidéo.</p>}

      {/* La liste de lecture d'abord, dans son ordre de passage : c'est ce qui
          tournera à l'écran, et l'ordre se lit sur les vignettes. */}
      <div className="tiles">
        {saver.videos.map((file, index) => (
          <article className="tile" key={file}>
            <div className="tile__media">
              <video src={`/media/screensaver/${encodeURIComponent(file)}`} muted preload="metadata" />
              <span className="tile__rank">{index + 1}</span>
            </div>

            <strong className="tile__name" title={file}>
              {file}
            </strong>

            <div className="tile__actions">
              <button
                type="button"
                className="btn btn--ghost btn--small"
                title="Monter dans la liste"
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                title="Descendre dans la liste"
                disabled={index === saver.videos.length - 1}
                onClick={() => move(index, 1)}
              >
                ↓
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => onChange({ videos: saver.videos.filter((v) => v !== file) })}
              >
                Retirer
              </button>
              <button
                type="button"
                className="btn btn--danger btn--small"
                onClick={() => onDelete(file)}
              >
                Supprimer
              </button>
            </div>
          </article>
        ))}

        {available
          .filter((file) => !saver.videos.includes(file))
          .map((file) => (
            <article className="tile tile--idle" key={file}>
              <div className="tile__media">
                <video
                  src={`/media/screensaver/${encodeURIComponent(file)}`}
                  muted
                  preload="metadata"
                />
              </div>

              <strong className="tile__name" title={file}>
                {file}
              </strong>
              <span className="card__hint">Sur la borne, hors liste de lecture.</span>

              <div className="tile__actions">
                <button
                  type="button"
                  className="btn btn--small"
                  onClick={() => onChange({ videos: [...saver.videos, file] })}
                >
                  Ajouter à la veille
                </button>
                <button
                  type="button"
                  className="btn btn--danger btn--small"
                  onClick={() => onDelete(file)}
                >
                  Supprimer
                </button>
              </div>
            </article>
          ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

const LIGHTING_MODES: { value: LightingMode; label: string; hint: string }[] = [
  {
    value: 'ring',
    label: 'Anneau lumineux',
    hint: 'Allumé dès le début du décompte autour du retour caméra, à pleine intensité au « 0 ». Lumière douce, sans surexposition.',
  },
  {
    value: 'screen',
    label: 'Écran blanc au « 0 »',
    hint: 'L’ancien flash : tout l’écran en blanc au moment de la photo. Éblouit, et surexpose souvent les visages.',
  },
  { value: 'off', label: 'Aucun', hint: 'L’éclairage vient du stand et du ruban LED.' },
];

/**
 * Éclairage donné par l'écran de la borne pendant la prise de vue. L'anneau
 * remplace l'ancien écran blanc : allumé dès la première seconde, il laisse à la
 * caméra le temps de régler son exposition.
 */
function LightingSection({
  settings,
  onChange,
}: {
  settings: AdminSettings;
  onChange: (patch: Partial<LightingSettings>) => void;
}) {
  const lighting = settings.lighting;
  const mode = LIGHTING_MODES.find((m) => m.value === lighting.mode) ?? LIGHTING_MODES[0];
  const stroke = lighting.thickness * 900;
  const radius = 450 - stroke / 2 - 12;

  return (
    <section className="card card--side">
      <header>
        <h2>Éclairage à l’écran</h2>
      </header>

      <p className="card__hint">{mode.hint}</p>

      {lighting.mode === 'ring' && (
        <svg className="ring-preview" viewBox="0 0 1600 900" aria-hidden>
          <rect width="1600" height="900" fill="#1b2330" />
          <circle
            cx="800"
            cy="450"
            r={radius}
            fill="none"
            stroke={lighting.color}
            strokeWidth={stroke}
            opacity={lighting.intensity}
          />
        </svg>
      )}

      <div className="field-grid">
        <label className="field--wide">
          Mode
          <select
            value={lighting.mode}
            onChange={(e) => onChange({ mode: e.target.value as LightingMode })}
          >
            {LIGHTING_MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>

        {lighting.mode === 'ring' && (
          <>
            <label>
              Couleur
              <input
                type="color"
                value={lighting.color}
                onChange={(e) => onChange({ color: e.target.value })}
              />
            </label>
            <label>
              Intensité pendant le décompte — {Math.round(lighting.intensity * 100)} %
              <input
                type="range"
                min={0.2}
                max={1}
                step={0.05}
                value={lighting.intensity}
                onChange={(e) => onChange({ intensity: Number(e.target.value) })}
              />
            </label>
            <label>
              Épaisseur — {Math.round(lighting.thickness * 100)} % de l’écran
              <input
                type="range"
                min={0.03}
                max={0.2}
                step={0.01}
                value={lighting.thickness}
                onChange={(e) => onChange({ thickness: Number(e.target.value) })}
              />
            </label>
          </>
        )}
      </div>
    </section>
  );
}

const WIPE_DIRECTIONS: { value: WipeDirection; label: string }[] = [
  { value: 'center', label: 'Depuis le milieu' },
  { value: 'start', label: 'Depuis le début du ruban' },
  { value: 'end', label: 'Depuis la fin du ruban' },
];

/**
 * Ruban LED de la borne, branché en USB sur un ESP32.
 *
 * Au repos il garde sa couleur ; à chaque seconde du décompte, la couleur de la
 * seconde se déroule ; au « 0 », il s'allume en plein pour éclairer la photo.
 * Le nombre de LED et la broche se règlent sur la borne (Ctrl+F9) : ils
 * dépendent du câblage, pas du client.
 */
function LedSection({
  settings,
  booth,
  onChange,
  onTest,
}: {
  settings: AdminSettings;
  booth: BoothPresence | null;
  onChange: (patch: Partial<LedSettings>) => void;
  onTest: () => void;
}) {
  const leds = settings.leds;
  const colors = leds.countdownColors;
  const controller = booth?.leds;
  const canTest = Boolean(booth?.online && controller?.connected);

  const setColor = (index: number, value: string) =>
    onChange({ countdownColors: colors.map((c, i) => (i === index ? value : c)) });

  return (
    <section className="card card--wide">
      <header>
        <h2>Ruban LED</h2>
        <Toggle checked={leds.enabled} onChange={(enabled) => onChange({ enabled })} />
      </header>

      <p className="card__hint">
        Au repos, le ruban garde sa couleur. Pendant le décompte, la couleur de chaque seconde se
        déroule sur le ruban. Au « 0 », il s’allume en plein pour éclairer les visages au moment
        de la photo, puis redescend vers le repos.
      </p>

      <StripPreview leds={leds} />

      <div className="field-grid">
        <label>
          Couleur au repos
          <span className="color-field">
            <input
              type="color"
              value={leds.idleColor}
              onChange={(e) => onChange({ idleColor: e.target.value })}
            />
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={() => onChange({ idleColor: '#000000' })}
            >
              Éteint
            </button>
          </span>
        </label>

        <label>
          Couleur du flash
          <input
            type="color"
            value={leds.flashColor}
            onChange={(e) => onChange({ flashColor: e.target.value })}
          />
        </label>

        <div className="field--wide led-colors">
          <span className="led-colors__title">
            Couleurs du décompte — la dernière est celle du « 1 »
          </span>
          <div className="led-colors__list">
            {colors.map((color, index) => (
              <span className="led-colors__item" key={index}>
                <span className="led-colors__second">{colors.length - index}</span>
                <input type="color" value={color} onChange={(e) => setColor(index, e.target.value)} />
                {colors.length > 1 && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    title="Retirer cette couleur"
                    onClick={() => onChange({ countdownColors: colors.filter((_, i) => i !== index) })}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {colors.length < MAX_COUNTDOWN_COLORS && (
              <button
                type="button"
                className="btn btn--ghost btn--small"
                title="Ajoute une seconde en tête de décompte"
                onClick={() => onChange({ countdownColors: [colors[0], ...colors] })}
              >
                + seconde
              </button>
            )}
          </div>
          <span className="card__hint">
            Un décompte plus long que la liste reprend la première couleur.
          </span>
        </div>

        <label>
          Fond entre deux secondes
          <input
            type="color"
            value={leds.countdownBackground}
            onChange={(e) => onChange({ countdownBackground: e.target.value })}
          />
        </label>

        <label>
          Déroulé
          <select
            value={leds.wipeDirection}
            onChange={(e) => onChange({ wipeDirection: e.target.value as WipeDirection })}
          >
            {WIPE_DIRECTIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          Durée du déroulé — {leds.wipeMs} ms
          <input
            type="range"
            min={150}
            max={950}
            step={50}
            value={leds.wipeMs}
            onChange={(e) => onChange({ wipeMs: Number(e.target.value) })}
          />
        </label>

        <label>
          Luminosité repos et décompte — {Math.round(leds.brightness * 100)} %
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={leds.brightness}
            onChange={(e) => onChange({ brightness: Number(e.target.value) })}
          />
        </label>

        <label>
          Luminosité du flash — {Math.round(leds.flashBrightness * 100)} %
          <input
            type="range"
            min={0.1}
            max={1}
            step={0.05}
            value={leds.flashBrightness}
            onChange={(e) => onChange({ flashBrightness: Number(e.target.value) })}
          />
        </label>
      </div>

      <div className="led-schedule">
        <label className="field--check">
          <input
            type="checkbox"
            checked={leds.schedule.enabled}
            onChange={(e) => onChange({ schedule: { ...leds.schedule, enabled: e.target.checked } })}
          />
          Éteindre le ruban la nuit
        </label>
        {leds.schedule.enabled && (
          <div className="led-schedule__times">
            <label>
              Allumé à
              <input
                type="time"
                value={leds.schedule.on}
                onChange={(e) => e.target.value && onChange({ schedule: { ...leds.schedule, on: e.target.value } })}
              />
            </label>
            <label>
              Éteint à
              <input
                type="time"
                value={leds.schedule.off}
                onChange={(e) => e.target.value && onChange({ schedule: { ...leds.schedule, off: e.target.value } })}
              />
            </label>
          </div>
        )}
        <span className="card__hint">
          {leds.schedule.enabled
            ? `Allumé de ${leds.schedule.on} à ${leds.schedule.off}, à l’heure de la borne${
                leds.schedule.off < leds.schedule.on ? ' (la plage passe minuit)' : ''
              }. Hors de la plage, le ruban reste éteint au repos ; une photo prise quand même garde son décompte et son flash.`
            : 'Le ruban reste allumé en permanence au repos.'}
        </span>
      </div>

      <div className="card__actions led-test">
        <button type="button" className="btn btn--small" disabled={!canTest} onClick={onTest}>
          Tester sur la borne
        </button>
        <span className="card__hint">
          {!booth?.online
            ? 'Borne hors ligne.'
            : controller?.connected
              ? `Contrôleur sur ${controller.port} — joue un décompte et un flash avec ces couleurs.`
              : controller?.detail || 'La borne n’a pas encore publié l’état de son ruban.'}
        </span>
      </div>
    </section>
  );
}

const PREVIEW_LEDS = 60;
const PREVIEW_IDLE_MS = 1400;
const PREVIEW_HOLD_MS = 870;
const PREVIEW_FADE_MS = 450;
const PREVIEW_FRONT = 3;

const toRgb = (hex: string) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const mix = (a: number[], b: number[], t: number) => a.map((v, i) => v + (b[i] - v) * t);

/**
 * Aperçu du ruban, rejoué en boucle : repos, décompte de trois secondes, flash,
 * retour au repos. Même calcul que le firmware — front adouci sur quelques LED,
 * déroulé ralenti en fin de course — pour que les couleurs se jugent ici comme
 * elles se verront sur le stand.
 */
function StripPreview({ leds }: { leds: LedSettings }) {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [label, setLabel] = useState('Repos');
  const settingsRef = useRef(leds);
  settingsRef.current = leds;

  useEffect(() => {
    let frame = 0;
    const started = performance.now();
    const cycle = PREVIEW_IDLE_MS + 3000 + PREVIEW_HOLD_MS + PREVIEW_FADE_MS;

    const draw = (now: number) => {
      const el = canvas.current;
      const ctx = el?.getContext('2d');
      frame = requestAnimationFrame(draw);
      if (!el || !ctx) return;

      const s = settingsRef.current;
      const lit = (hex: string, factor: number) =>
        s.enabled ? toRgb(`#${scaleColor(hex, factor)}`) : [0, 0, 0];
      const idle = lit(s.idleColor, s.brightness);
      const t = (now - started) % cycle;
      const n = PREVIEW_LEDS;
      const pixels: number[][] = [];
      let phase = 'Repos';

      if (t < PREVIEW_IDLE_MS) {
        for (let i = 0; i < n; i++) pixels.push(idle);
      } else if (t < PREVIEW_IDLE_MS + 3000) {
        const second = Math.floor((t - PREVIEW_IDLE_MS) / 1000);
        const remaining = 3 - second;
        phase = String(remaining);
        const local = (t - PREVIEW_IDLE_MS) % 1000;
        const color = lit(countdownColorFor(s, remaining), s.brightness);
        const background = lit(s.countdownBackground, s.brightness);
        const p = Math.min(1, local / s.wipeMs);
        const eased = 1 - (1 - p) * (1 - p);
        const length = s.wipeDirection === 'center' ? n / 2 : n;
        const front = eased * (length + 2 * PREVIEW_FRONT) - PREVIEW_FRONT;
        for (let i = 0; i < n; i++) {
          const pos =
            s.wipeDirection === 'center'
              ? Math.abs(i - (n - 1) / 2)
              : s.wipeDirection === 'end'
                ? n - 1 - i
                : i;
          const delta = pos - front;
          const w =
            delta <= -PREVIEW_FRONT ? 1 : delta >= PREVIEW_FRONT ? 0 : (PREVIEW_FRONT - delta) / (2 * PREVIEW_FRONT);
          pixels.push(mix(background, color, w));
        }
      } else {
        phase = 'Flash';
        const flash = lit(s.flashColor, s.flashBrightness);
        const since = t - PREVIEW_IDLE_MS - 3000;
        const c = since < PREVIEW_HOLD_MS ? flash : mix(flash, idle, (since - PREVIEW_HOLD_MS) / PREVIEW_FADE_MS);
        for (let i = 0; i < n; i++) pixels.push(c);
      }

      setLabel((current) => (current === phase ? current : phase));

      const ratio = window.devicePixelRatio || 1;
      const width = el.clientWidth * ratio;
      const height = el.clientHeight * ratio;
      if (el.width !== width) el.width = width;
      if (el.height !== height) el.height = height;

      ctx.fillStyle = '#05070b';
      ctx.fillRect(0, 0, width, height);
      const step = width / n;
      const size = Math.min(step * 0.62, height * 0.5);
      for (let i = 0; i < n; i++) {
        const [r, g, b] = pixels[i].map(Math.round);
        const x = step * (i + 0.5);
        const y = height / 2;
        const glow = ctx.createRadialGradient(x, y, 0, x, y, step * 1.4);
        glow.addColorStop(0, `rgba(${r},${g},${b},0.45)`);
        glow.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.fillStyle = glow;
        ctx.fillRect(x - step * 1.4, 0, step * 2.8, height);
        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.fillRect(x - size / 2, y - size / 2, size, size);
      }
    };

    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div className="strip-preview">
      <canvas ref={canvas} className="strip-preview__canvas" aria-hidden />
      <span className="strip-preview__label">{label}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span />
      {checked ? 'Activé' : 'Désactivé'}
    </label>
  );
}

/**
 * Bouton d'envoi avec avancement.
 *
 * Une vidéo de veille pèse couramment quelques centaines de méga-octets : sans
 * barre, l'admin paraît figée le temps du transfert et l'on finit par cliquer
 * une seconde fois.
 */
function UploadButton({
  accept,
  label,
  onFile,
}: {
  accept: string;
  label: string;
  onFile: (file: File, onProgress: (ratio: number) => void) => Promise<unknown>;
}) {
  const input = useRef<HTMLInputElement | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [name, setName] = useState('');

  const send = async (file: File) => {
    setName(file.name);
    setProgress(0);
    try {
      await onFile(file, setProgress);
    } finally {
      setProgress(null);
    }
  };

  return (
    <>
      <input
        ref={input}
        type="file"
        accept={accept}
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void send(file);
          event.target.value = '';
        }}
      />
      {progress === null ? (
        <button type="button" className="btn btn--small" onClick={() => input.current?.click()}>
          {label}
        </button>
      ) : (
        <span className="upload" title={name}>
          <span className="upload__bar">
            <span style={{ width: `${Math.round(progress * 100)}%` }} />
          </span>
          {/* 100 % atteint, le serveur vérifie encore le fichier et publie la
              liste à la borne : on le dit plutôt que de laisser une barre
              pleine qui ne bouge plus. */}
          <span className="upload__label">
            {progress >= 1 ? 'Enregistrement…' : `${Math.round(progress * 100)} %`}
          </span>
        </span>
      )}
    </>
  );
}
